import colors from "react-native-ui-lib/src/style/colors";
import PaymentMethod from "../../../../../../utilities/data/model/PaymentMethod";
import Sale from "../../../../../../utilities/data/model/Sale";
import { usePaymentControllerProvider } from "../../../../utils/payment-controller";
import { PaymentScreenProps } from "../../PaymentScreen";
import React from "react";
import { Spacings, View, Image } from "react-native-ui-lib";
import {
  Button,
  LoadingIndicator,
  Stack,
  Text,
} from "@towersystems/roam-common-native";
import {
  clearSmartPayPaymentSession,
  createTransaction,
  generateDeviceId,
  pollForOutcome,
  POS_VENDOR_NAME,
  retrieveSalePaymentSession,
  saveSmartPayPaymentSession,
  TransactionOutcome,
  TransactionRequestResponse,
  TransactionStatus,
  TransactionType,
} from "../../../../../../utilities/smartpay";
import Terminal from "../../../../../../utilities/data/model/Terminal";
import { GlobalSettings } from "@towersystems/roam-common/lib/generated-types";
import { convertCentsToDollars } from "../../../../../../utilities/linkly/utils";
import { AddPaymentOptions } from "../../../../utils/payment-controller/types";
import { toHtmlPage } from "../../../../../../utilities/to-html-page";
import { useAssets } from "expo-asset";

export interface MakeSmartPayPaymentProps {
  paymentMethod: PaymentMethod;
  currentTerminal: Terminal;
  globalSettings: GlobalSettings;
  sale: Sale;
  navigation: PaymentScreenProps["navigation"];
}

export const MakeSmartPayPayment = ({
  navigation,
  currentTerminal,
  paymentMethod,
  globalSettings,
  sale,
}: MakeSmartPayPaymentProps) => {
  const [transactionResult, setTransactionResult] =
    React.useState<TransactionRequestResponse | null>(null);

  const [showRecoveryButton, setShowRecoveryButton] =
    React.useState<boolean>(false);

  const [showCompletedButton, setShowCompletedButton] =
    React.useState<boolean>(false);

  const {
    validTenderedAmount,
    addPayment,
    tenderedAmountAsInteger,
    isRefundAmount,
  } = usePaymentControllerProvider();

  const [displayText, setDisplayText] = React.useState<string[][]>([
    ["Initializing SmartPay..."],
  ]);

  const [errorText, setErrorText] = React.useState<string | null>(null);

  const [showLoading] = React.useState<boolean>(false);

  const [assets] = useAssets([
    require("../../../../../../../assets/images/smartpay-logo-white.png"),
  ]);

  React.useEffect(() => {
    navigation.setOptions({
      // headerLeft: () => null,
      cardStyle: {
        backgroundColor: isRefundAmount ? colors.tyroRefund : colors.tyro,
      },
      headerStyle: {
        backgroundColor: isRefundAmount ? colors.tyroRefund : colors.tyro,
        borderBottomColor: "rgba(255,255,255,0.2)",
      },
      headerTitle: () => null,
    });
  }, [colors, isRefundAmount]);

  // Handle polling for transaction status
  React.useEffect(() => {
    (async () => {
      // check if can recover from a previous transaction
      const maybeExistingSession = await retrieveSalePaymentSession({
        saleId: sale.id,
      });

      if (maybeExistingSession) {
        setDisplayText(() => [
          [
            "Failed to retreive a response from SmartPay for last transaction.",
            "Attempt recovery?",
          ],
        ]);
        setShowRecoveryButton(true);
        return;
      }
      await beginTransaction();
    })();
  }, []);

  React.useEffect(() => {
    const pollingUrl = transactionResult?.data?.PollingUrl;
    let interval: ReturnType<typeof setInterval> | undefined;

    if (pollingUrl) {
      interval = setInterval(() => {
        pollForOutcome(pollingUrl)
          .then((response) => {
            if (response.transactionStatus === TransactionStatus.Completed) {
              clearInterval(interval);
              setTransactionResult(response);
            } else if (response.transactionStatus === "PENDING") {
              if (
                response.data &&
                response.data.TransactionResult ===
                  TransactionOutcome.OK_DELAYED
              ) {
                setDisplayText((prev) => [
                  ...prev,
                  ["Transaction is taking longer than expected."],
                ]);
              }
            } else {
              setErrorText("Transaction failed or was cancelled.");
              clearInterval(interval);
            }
          })
          .catch(() => {
            setErrorText("Polling failed. Trying again...");
            //clearInterval(interval);
          });
      }, 2500);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [transactionResult?.data?.PollingUrl]);

  React.useEffect(() => {
    if (transactionResult) {
      // If the transaction result is completed, handle the outcome
      if (transactionResult.transactionStatus === TransactionStatus.Completed) {
        handleCompletedTransactionResult(transactionResult);
      }
    }
  }, [transactionResult]);

  const beginTransaction = async () => {
    if (validTenderedAmount && tenderedAmountAsInteger) {
      try {
        // generate a device ID based on the terminal and payment method
        // this is used to identify the device in the SmartPay system
        const posRegisterId = generateDeviceId(
          currentTerminal.id,
          paymentMethod.id
        );

        const posBusinessName = globalSettings.businessName ?? "";
        const posVendor = POS_VENDOR_NAME;

        // use the createTransaction function to initiate the transaction
        const result = await createTransaction(
          tenderedAmountAsInteger,
          isRefundAmount
            ? TransactionType.CardRefund
            : TransactionType.CardPurchase,
          {
            posBusinessName: posBusinessName,
            posVendorName: posVendor,
            registerId: posRegisterId,
          }
        );

        if ("error" in result) {
          setErrorText(result.error);
        }

        // start polling for the transaction status
        if ("data" in result) {
          if (result.data?.PollingUrl) {
            await saveSmartPayPaymentSession({
              saleId: sale.id,
              transactionRequestResponse: result,
            });
            setDisplayText((prev) => [
              ...prev,
              ["Transaction Started. Polling for transaction status"],
            ]);
            setTransactionResult(result);
          }
        }
      } catch (error) {}
    }
  };

  const handleCompletedTransactionResult = React.useCallback(
    async (response: TransactionRequestResponse) => {
      if (
        !response.data ||
        response.transactionStatus !== TransactionStatus.Completed
      ) {
        setErrorText("Transaction failed or was cancelled.");
        return;
      }

      const transactionOutcome = response.data.TransactionResult;

      switch (transactionOutcome) {
        case TransactionOutcome.OK_ACCEPTED:
          setDisplayText([["Transaction Complete"]]);
          await addSalePayment(response);
          await clearSmartPayPaymentSession();
          break;
        case TransactionOutcome.OK_DELAYED:
          setDisplayText([
            [
              "Transaction is taking longer than expected.",
              "Please wait for the transaction to complete.",
            ],
          ]);
          await clearSmartPayPaymentSession();
        case TransactionOutcome.CANCELLED:
          setErrorText("Transaction was cancelled by the user.");
          await clearSmartPayPaymentSession();
          break;
        case TransactionOutcome.OK_DECLINED:
          await clearSmartPayPaymentSession();
          setErrorText("Transaction was declined.");
          break;

        case TransactionOutcome.FAILED:
          await clearSmartPayPaymentSession();
          setErrorText("Transaction failed. Please try again.");
          break;
        case TransactionOutcome.DeviceOffline:
          await clearSmartPayPaymentSession();
          setErrorText(
            "Transaction Cancelled! Please check if the device is powered on and online."
          );
          break;
        case TransactionOutcome.FAILED_INTERFACE:
          await clearSmartPayPaymentSession();
          setErrorText(
            "Transaction failed due to an interface error. Please try again."
          );
          break;
        default:
          await clearSmartPayPaymentSession();
          setErrorText("Unknown transaction outcome. Please try again.");
          break;
      }
    },
    []
  );

  const addSalePayment = React.useCallback(
    async (response: TransactionRequestResponse) => {
      try {
        if (
          !response.data ||
          response.transactionStatus !== TransactionStatus.Completed
        ) {
          setErrorText("Something went wrong.");
          return;
        }

        const transactionType = response.data.Function;

        if (
          false ==
          [TransactionType.CardPurchase, TransactionType.CardRefund].includes(
            transactionType
          )
        ) {
          setErrorText(
            `Transaction type "${transactionType}" is not supported.`
          );
          return;
        }

        const purchaseAmountAsInteger = parseInt(
          response.data.AmountTotal ?? "0"
        );
        const purchaseAmountAsDollars = convertCentsToDollars(
          purchaseAmountAsInteger
        );

        const maybeCustomerReceipt = response.data.Receipt;

        const maybeSurchargeAmount =
          transactionType == TransactionType.CardPurchase &&
          response.data.AmountSurcharge
            ? convertCentsToDollars(parseInt(response.data.AmountSurcharge))
            : undefined;

        const paymentAmount =
          transactionType === TransactionType.CardRefund
            ? -Math.abs(purchaseAmountAsDollars)
            : purchaseAmountAsDollars;

        const paymentInputOptions: AddPaymentOptions = {
          tenderedAmount: paymentAmount,
          surcharge: maybeSurchargeAmount,
          name: "SmartPay Payment",
          transactionId: response.data.RequestId,
          metadata: {
            response,
            customerReceipt: maybeCustomerReceipt
              ? toHtmlPage(maybeCustomerReceipt)
              : undefined,
          },
        };

        await addPayment(paymentMethod, paymentInputOptions);

        setShowCompletedButton(true);
        // await onDonePressed();
      } catch (error) {
        console.error("Error adding sale payment:", error);
        setErrorText("Failed to add payment. Check terminal for correctness.");
      }
    },
    [sale]
  );

  const onDonePressed = async () => {
    const paidOff = await sale.balancePaidOff_ASYNC();

    if (paidOff) {
      navigation.navigate("CheckoutSummaryRootScreen", {
        screen: "CheckoutReviewScreen",
      });
    } else {
      navigation.navigate("SelectPaymentMethodScreen");
    }
  };

  const showCompletedButtonMarkup = showCompletedButton ? (
    <Button
      variant="outline"
      style={{ minWidth: 120 }}
      onPress={async () => {
        onDonePressed();
      }}
    >
      <Text style={{ color: "#fff", fontFamily: "Gilmer-Bold", fontSize: 17 }}>
        Done
      </Text>
    </Button>
  ) : null;

  const recoveryButtonMarkup = showRecoveryButton ? (
    <>
      <Button
        variant="outline"
        style={{ minWidth: 120 }}
        onPress={async () => {
          await clearSmartPayPaymentSession();
          setDisplayText([]);
          setShowRecoveryButton(false);
          await beginTransaction();
        }}
      >
        <Text
          style={{ color: "#fff", fontFamily: "Gilmer-Bold", fontSize: 17 }}
        >
          New Transaction
        </Text>
      </Button>
      <Button
        variant="outline"
        style={{ minWidth: 120 }}
        onPress={async () => {
          const maybeExistingSession = await retrieveSalePaymentSession({
            saleId: sale.id,
          });
          if (maybeExistingSession) {
            setDisplayText((prev) => [
              ...prev,
              ["Attempting to recover previous transaction..."],
            ]);
            setShowRecoveryButton(false);
            setTransactionResult(maybeExistingSession);
          }
        }}
      >
        <Text
          style={{ color: "#fff", fontFamily: "Gilmer-Bold", fontSize: 17 }}
        >
          Retry Transaction
        </Text>
      </Button>
    </>
  ) : null;

  const errorButtonMarkup = errorText ? (
    <Button
      variant="outline"
      style={{ minWidth: 120 }}
      onPress={() => navigation.goBack()}
    >
      <Text style={{ color: "#fff", fontFamily: "Gilmer-Bold", fontSize: 17 }}>
        Go back
      </Text>
    </Button>
  ) : null;

  const btnMarkup = (recoveryButtonMarkup ||
    errorButtonMarkup ||
    showCompletedButtonMarkup) && (
    <View style={{ marginTop: Spacings.s4 }}>
      <Stack centerHorizontal>
        {errorButtonMarkup}
        {recoveryButtonMarkup}
        {showCompletedButtonMarkup}
      </Stack>
    </View>
  );

  const errorTextMarkup = errorText ? (
    <Text
      style={{
        textAlign: "center",
        fontSize: 18,
        marginBottom: Spacings.s6,
        color: colors.white,
      }}
    >
      {errorText}
    </Text>
  ) : null;

  const displayTextMarkup =
    displayText.length >= 1 &&
    displayText[displayText.length - 1].map((text, index) => (
      <Text
        style={{
          textAlign: "center",
          fontSize: 18,
          marginBottom: Spacings.s6,
          color: colors.white,
        }}
        key={index}
      >
        {text}
      </Text>
    ));

  const loadingMarkup =
    showLoading || (!btnMarkup && !displayTextMarkup && !errorTextMarkup) ? (
      <LoadingIndicator />
    ) : undefined;

  return (
    <View
      flex
      style={{
        width: "100%",
        height: "100%",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <View style={{ justifyContent: "center", alignItems: "center" }}>
        {assets ? (
          <View style={{ marginBottom: Spacings.s5 * 2 }}>
            <Image style={{ width: 230, height: 45 }} source={assets[0]} />
          </View>
        ) : null}
        {loadingMarkup}
        {errorTextMarkup || displayTextMarkup}
        {btnMarkup}
      </View>
    </View>
  );
};
